import type {
  Die,
  GameAction,
  GameConfig,
  GameEvent,
  GameState,
  PlayerState,
  ScoreColumn,
  ScoreContext,
  VariantDef,
} from './types';
import { blankCard, cardTotals, columnTotals, isCardFull } from './scorecard';
import { isAllSame } from './dice';
import { newSeed, rollValue, shortId } from './rng';
import { getVariant } from './variants';
import { canSpell, isWord } from './words';

const FEED_CAP = 40;

export function totalRounds(variant: VariantDef): number {
  return variant.categories.length * variant.columns.length;
}

export function newGame(config: GameConfig): GameState {
  const variant = getVariant(config.variantId);
  const players: PlayerState[] = config.players.map(p => ({
    ...p,
    card: blankCard(variant),
    streak: 0,
    powers: variant.powers
      ? { ...variant.powers }
      : { reroll: 0, flip: 0, nudge: 0 },
  }));
  const first = players[0];
  return {
    id: shortId(),
    variantId: variant.id,
    seed: config.seed ?? newSeed(),
    createdAt: Date.now(),
    players,
    round: 0,
    turn: 0,
    dice: Array.from({ length: variant.diceCount }, () => ({
      value: 1,
      held: false,
    })),
    rollsUsed: 0,
    extraRolls: 0,
    rollCounter: 0,
    phase: 'playing',
    winnerId: null,
    lastEvents: first
      ? [{ type: 'turnStart', playerId: first.id, text: `${first.name} is up` }]
      : [],
    feed: [],
    version: 0,
  };
}

export function currentPlayer(state: GameState): PlayerState {
  const p = state.players[state.turn];
  if (!p) throw new Error('No current player');
  return p;
}

export function variantOf(state: GameState): VariantDef {
  return getVariant(state.variantId);
}

export function rollsAllowed(state: GameState): number {
  return variantOf(state).maxRolls + state.extraRolls;
}

export function canRoll(state: GameState): boolean {
  return state.phase === 'playing' && state.rollsUsed < rollsAllowed(state);
}

export function canHold(state: GameState): boolean {
  return (
    state.phase === 'playing' &&
    state.rollsUsed > 0 &&
    state.rollsUsed < rollsAllowed(state)
  );
}

/** Whether the most recent score can still be taken back (until anyone rolls). */
export function canUndoScore(state: GameState): boolean {
  return state.phase === 'playing' && state.scoreUndo != null;
}

/** The box the undoable score went into, or null if there is none. */
export function lastScoredBox(
  state: GameState
): { playerId: string; categoryId: string; col: number } | null {
  const snap = state.scoreUndo;
  if (!snap) return null;
  const before = snap.players.find(p => p.id === snap.playerId);
  const after = state.players.find(p => p.id === snap.playerId);
  if (!before || !after) return null;
  for (const [col, column] of after.card.cols.entries()) {
    for (const [categoryId, value] of Object.entries(column.boxes)) {
      if (value != null && before.card.cols[col]?.boxes[categoryId] == null)
        return { playerId: snap.playerId, categoryId, col };
    }
  }
  return null;
}

export function diceValues(state: GameState): number[] {
  return state.dice.map(d => d.value);
}

export function diceLetters(state: GameState): string[] | undefined {
  const faces = variantOf(state).letterFaces;
  if (!faces) return undefined;
  return state.dice.map((d, i) => faces[i]?.[d.value] ?? '?');
}

function jokerActive(
  variant: VariantDef,
  dice: number[],
  column: ScoreColumn
): boolean {
  if (!variant.fiveKind?.joker) return false;
  if (!isAllSame(dice)) return false;
  return column.boxes[variant.fiveKind.categoryId] != null;
}

export function buildScoreContext(
  state: GameState,
  col: number,
  word?: string
): ScoreContext {
  const variant = variantOf(state);
  const player = currentPlayer(state);
  const column = player.card.cols[col] ?? player.card.cols[0]!;
  const dice = diceValues(state);
  const letters = diceLetters(state);
  const w = word?.toUpperCase();
  const wordValid =
    w != null && letters != null ? canSpell(w, letters) && isWord(w) : false;
  return {
    dice,
    letters,
    rollsUsed: state.rollsUsed,
    column,
    card: player.card,
    variant,
    word: w,
    wordValid,
    joker: jokerActive(variant, dice, column),
  };
}

export interface BoxOption {
  categoryId: string;
  col: number;
  /** Points this box would score right now. */
  score: number;
  /** Whether the box is open. */
  open: boolean;
  /** Whether the rules allow choosing it right now. */
  legal: boolean;
}

/** Every box on the card with its current preview score and legality. */
export function boxOptions(state: GameState, word?: string): BoxOption[] {
  const variant = variantOf(state);
  const player = currentPlayer(state);
  const out: BoxOption[] = [];
  const rolled = state.rollsUsed > 0;

  player.card.cols.forEach((column, col) => {
    const ctx = buildScoreContext(state, col, word);
    const open = variant.categories.filter(c => column.boxes[c.id] == null);
    const openIds = new Set(open.map(c => c.id));

    // Determine which open boxes are legal under variant rules.
    let legalIds = new Set(openIds);
    if (variant.sequential) {
      const next = open[0];
      legalIds = next ? new Set([next.id]) : new Set();
    }
    if (ctx.joker && variant.fiveKind) {
      const face = ctx.dice[0] ?? 0;
      const upperId = `n${face}`;
      const upperSet = new Set(variant.upperCategoryIds);
      if (openIds.has(upperId)) {
        legalIds = new Set([upperId]);
      } else {
        const lowerOpen = [...openIds].filter(id => !upperSet.has(id));
        if (lowerOpen.length > 0) legalIds = new Set(lowerOpen);
        // else: any open upper box (they all score 0 here)
      }
    }

    for (const c of variant.categories) {
      const isOpen = openIds.has(c.id);
      let score = 0;
      let legal = false;
      if (isOpen && rolled) {
        score = c.score(ctx);
        legal = legalIds.has(c.id);
      }
      out.push({ categoryId: c.id, col, score, open: isOpen, legal });
    }
  });
  return out;
}

function pushEvents(state: GameState, events: GameEvent[]): GameState {
  const feed = [...state.feed, ...events].slice(-FEED_CAP);
  return { ...state, lastEvents: events, feed, version: state.version + 1 };
}

function rollDice(state: GameState, variant: VariantDef): Die[] {
  const faces = variant.letterFaces ? 6 : 6;
  const first = state.rollsUsed === 0;
  return state.dice.map((d, i) => {
    if (!first && d.held) return d;
    const value = variant.sharedRolls
      ? rollValue(state.seed, faces, state.round, state.rollsUsed, i)
      : rollValue(state.seed, faces, state.rollCounter, i);
    const v = variant.letterFaces ? value - 1 : value;
    return { value: v, held: false };
  });
}

function advanceTurn(state: GameState, variant: VariantDef): GameState {
  let turn = state.turn + 1;
  let round = state.round;
  if (turn >= state.players.length) {
    turn = 0;
    round += 1;
  }
  const everyoneDone = state.players.every(p => isCardFull(p.card));
  const next = state.players[turn];
  const dice = state.dice.map(d => ({ ...d, held: false }));
  if (everyoneDone || round >= totalRounds(variant) || !next) {
    return {
      ...state,
      turn,
      round,
      dice,
      rollsUsed: 0,
      extraRolls: 0,
      phase: 'finished',
    };
  }
  return {
    ...state,
    turn,
    round,
    dice,
    rollsUsed: 0,
    extraRolls: 0,
  };
}

export function reduce(state: GameState, action: GameAction): GameState {
  if (state.phase !== 'playing') return state;
  const variant = variantOf(state);
  const player = currentPlayer(state);

  switch (action.type) {
    case 'roll': {
      if (!canRoll(state)) return state;
      const dice = rollDice(state, variant);
      const next: GameState = {
        ...state,
        dice,
        rollsUsed: state.rollsUsed + 1,
        rollCounter: state.rollCounter + 1,
        // Once anyone rolls, the previous score is locked in.
        scoreUndo: null,
      };
      return pushEvents(next, [
        {
          type: 'roll',
          playerId: player.id,
          text: `${player.name} rolled`,
          value: next.rollsUsed,
        },
      ]);
    }

    case 'undoScore': {
      const snap = state.scoreUndo;
      if (!snap) return state;
      const scorer = snap.players.find(p => p.id === snap.playerId);
      const scored = state.feed.findLast(
        e => e.type === 'score' && e.playerId === snap.playerId
      );
      const what = scored?.categoryId
        ? ` ${scored.value ?? 0} in ${categoryName(variant, scored.categoryId)}`
        : ' a score';
      const { playerId: _, feed, ...restored } = snap;
      // Back to the scorer's turn with the same dice and rolls used.
      return pushEvents(
        { ...state, ...restored, feed, scoreUndo: null },
        scorer
          ? [
              {
                type: 'undoScore',
                playerId: scorer.id,
                text: `${scorer.name} took back${what}`,
              },
            ]
          : []
      );
    }

    case 'toggleHold': {
      if (!canHold(state)) return state;
      const die = state.dice[action.index];
      if (!die) return state;
      const dice = state.dice.map((d, i) =>
        i === action.index ? { ...d, held: !d.held } : d
      );
      return { ...state, dice, version: state.version + 1, lastEvents: [] };
    }

    case 'setHolds': {
      if (!canHold(state)) return state;
      if (action.held.length !== state.dice.length) return state;
      const dice = state.dice.map((d, i) => ({ ...d, held: !!action.held[i] }));
      return { ...state, dice, version: state.version + 1, lastEvents: [] };
    }

    case 'usePower': {
      if (!variant.powers) return state;
      const left = player.powers[action.power] ?? 0;
      if (left <= 0) return state;
      let dice = state.dice;
      let extraRolls = state.extraRolls;
      let text = '';
      if (action.power === 'reroll') {
        if (state.rollsUsed < rollsAllowed(state)) return state; // only when out of rolls
        extraRolls += 1;
        text = `${player.name} took an extra roll`;
      } else {
        if (state.rollsUsed === 0) return state;
        const idx = action.index ?? -1;
        const die = state.dice[idx];
        if (!die) return state;
        if (action.power === 'flip') {
          dice = state.dice.map((d, i) =>
            i === idx ? { ...d, value: 7 - d.value } : d
          );
          text = `${player.name} flipped a die to ${7 - die.value}`;
        } else {
          const delta = action.delta ?? 1;
          const v = die.value + delta;
          if (v < 1 || v > 6) return state;
          dice = state.dice.map((d, i) => (i === idx ? { ...d, value: v } : d));
          text = `${player.name} nudged a die to ${v}`;
        }
      }
      const players = state.players.map((p, i) =>
        i === state.turn
          ? { ...p, powers: { ...p.powers, [action.power]: left - 1 } }
          : p
      );
      // A power alters the dice/roll state, so the score undo is void.
      return pushEvents(
        { ...state, players, dice, extraRolls, scoreUndo: null },
        [{ type: 'power', playerId: player.id, text }]
      );
    }

    case 'setPlayerKind': {
      const idx = state.players.findIndex(p => p.id === action.playerId);
      const target = state.players[idx];
      if (!target || target.kind === action.kind) return state;
      const players = state.players.map((p, i) =>
        i === idx
          ? {
              ...p,
              kind: action.kind,
              ...(action.kind === 'ai'
                ? { aiLevel: action.aiLevel ?? p.aiLevel ?? 'normal' }
                : {}),
            }
          : p
      );
      const label =
        action.kind === 'ai'
          ? `${target.name} is now played by the computer`
          : `${target.name} is back in the seat`;
      return pushEvents({ ...state, players }, [
        { type: 'seat', playerId: target.id, text: label },
      ]);
    }

    case 'score': {
      if (state.rollsUsed === 0) return state;
      const col = action.col ?? 0;
      const option = boxOptions(state, action.word).find(
        o => o.categoryId === action.categoryId && o.col === col
      );
      if (!option || !option.legal) return state;
      const category = variant.categories.find(c => c.id === action.categoryId);
      const column = player.card.cols[col];
      if (!category || !column) return state;

      const ctx = buildScoreContext(state, col, action.word);
      const events: GameEvent[] = [];
      let raw = option.score;

      // Streak multiplier.
      let streak = player.streak;
      if (variant.streak) {
        if (raw > 0) {
          const stacks = Math.min(streak, variant.streak.maxStacks);
          const mult = 1 + stacks * variant.streak.step;
          raw = Math.round(raw * mult);
          streak += 1;
          if (stacks > 0)
            events.push({
              type: 'streak',
              playerId: player.id,
              text: `${player.name} is on a ${streak} streak (x${mult.toFixed(1)})`,
              value: streak,
            });
        } else if (streak > 0) {
          events.push({
            type: 'streakBroken',
            playerId: player.id,
            text: `${player.name}'s streak is over`,
          });
          streak = 0;
        }
      }

      const beforeTotals = columnTotals(column, variant, col);
      const boxes = { ...column.boxes, [category.id]: raw };
      let fiveKindBonuses = column.fiveKindBonuses;

      // Five-of-a-kind bonus (Yahtzee bonus).
      const fk = variant.fiveKind;
      if (fk && isAllSame(ctx.dice)) {
        const held = column.boxes[fk.categoryId];
        if (held != null && held > 0) {
          fiveKindBonuses += 1;
          events.push({
            type: 'fiveKindBonus',
            playerId: player.id,
            text: `${player.name} scored a bonus ${categoryName(variant, fk.categoryId)}: +${fk.bonus}`,
            value: fk.bonus,
          });
        }
      }
      const isFiveKindBox =
        variant.fiveKind?.categoryId === category.id ||
        variant.servedInstantWin === category.id;
      if (isFiveKindBox && raw > 0) {
        events.push({
          type: 'fiveKind',
          playerId: player.id,
          text: `${player.name} rolled a ${category.name}!`,
          value: raw,
          categoryId: category.id,
        });
      }

      const newColumn: ScoreColumn = { boxes, fiveKindBonuses };
      const afterTotals = columnTotals(newColumn, variant, col);
      if (afterTotals.upperBonus > beforeTotals.upperBonus) {
        events.push({
          type: 'upperBonus',
          playerId: player.id,
          text: `${player.name} earned the upper bonus: +${afterTotals.upperBonus}`,
          value: afterTotals.upperBonus,
        });
      }
      if (ctx.rollsUsed === 1 && raw > 0 && variant.servedInstantWin) {
        events.push({
          type: 'served',
          playerId: player.id,
          text: `${player.name} served ${category.name}`,
        });
      }

      const cols = player.card.cols.map((c, i) => (i === col ? newColumn : c));
      const players = state.players.map((p, i) =>
        i === state.turn ? { ...p, card: { cols }, streak } : p
      );
      events.unshift({
        type: 'score',
        playerId: player.id,
        text: `${player.name} scored ${raw} in ${category.name}`,
        value: raw,
        categoryId: category.id,
      });

      let next: GameState = { ...state, players };

      const instantWin =
        variant.servedInstantWin === category.id &&
        ctx.rollsUsed === 1 &&
        raw > 0;
      if (instantWin) {
        events.push({
          type: 'instantWin',
          playerId: player.id,
          text: `${player.name} wins with a served ${category.name}!`,
        });
        next = {
          ...next,
          phase: 'finished',
          winnerId: player.id,
          dice: next.dice.map(d => ({ ...d, held: false })),
        };
      } else {
        next = advanceTurn(next, variant);
      }
      // Single-level: a finished game is final, otherwise the scorer may take
      // it back until the next roll.
      next = {
        ...next,
        scoreUndo:
          next.phase === 'playing'
            ? {
                playerId: player.id,
                players: state.players,
                turn: state.turn,
                round: state.round,
                dice: state.dice,
                rollsUsed: state.rollsUsed,
                extraRolls: state.extraRolls,
                feed: state.feed,
              }
            : null,
      };

      if (next.phase === 'finished') {
        const winner = leader(next);
        if (!next.winnerId && winner) next = { ...next, winnerId: winner.id };
        events.push({
          type: 'gameOver',
          playerId: next.winnerId ?? player.id,
          text: 'Game over',
        });
      } else {
        const up = currentPlayer(next);
        events.push({
          type: 'turnStart',
          playerId: up.id,
          text: `${up.name} is up`,
        });
      }
      return pushEvents(next, events);
    }
  }
}

function categoryName(variant: VariantDef, id: string): string {
  return variant.categories.find(c => c.id === id)?.name ?? id;
}

export function scoreOf(state: GameState, playerIndex: number): number {
  const p = state.players[playerIndex];
  if (!p) return 0;
  return cardTotals(p.card, variantOf(state)).grand;
}

/** Player with the highest total (ties: earliest seat). */
export function leader(state: GameState): PlayerState | null {
  let best: PlayerState | null = null;
  let bestScore = -1;
  state.players.forEach((p, i) => {
    const s = scoreOf(state, i);
    if (s > bestScore) {
      bestScore = s;
      best = p;
    }
  });
  return best;
}

export function standings(
  state: GameState
): { player: PlayerState; score: number; rank: number }[] {
  const rows = state.players.map((player, i) => ({
    player,
    score: scoreOf(state, i),
    rank: 0,
  }));
  const sorted = [...rows].sort((a, b) => b.score - a.score);
  let rank = 0;
  let prev = -1;
  sorted.forEach((r, i) => {
    if (r.score !== prev) rank = i + 1;
    r.rank = rank;
    prev = r.score;
  });
  return sorted;
}
